/**
 * @license
 * Copyright (c) 2023 Entelechy Corporation. All rights reserved.
 */

import { css, html } from "lit"
import { customElement, property } from 'lit/decorators.js'
import { supabase,  } from "../Supabase"
import { MenuItem } from "./ec-menu"
import { Router } from '@vaadin/router'
import { ECComponent } from "./ec-component"
import { consume } from '@lit/context'
import { User, userContext } from "./ec-app"
import { ConfigType } from "../ConfigType"

declare const CONFIG: ConfigType

@customElement('ec-header')
export class ECHeader extends ECComponent {
  /**
   * Name of the application
   */
  @property({type: String })
  title: string

  @consume({context: userContext, subscribe: true})
  @property({attribute: false})
  public user?: User;

  userMenuItems: MenuItem[] = [
    {
      icon: 'person_add',
      name: 'Sign up',
      title: 'Sign up',
      signedInOnly: false,
      signedOutOnly: true,
      debugOnly: false,
      action: this.signUp
    }, {
      icon: 'login',
      name: 'Sign in',
      title: 'Sign in',
      signedInOnly: false,
      signedOutOnly: true,
      debugOnly: false,
      action: this.signIn
    }, {
      icon: 'settings',
      name: 'Settings',
      title: 'Settings',
      signedInOnly: true,
      signedOutOnly: false,
      debugOnly: false,
      action: this.settings
    }, {
      icon: 'logout',
      name: 'Sign out',
      title: 'Sign out',
      signedInOnly: true,
      signedOutOnly: false,      
      debugOnly: false,
      action: this.signOut
    }, {
      icon: 'pest_control',
      name: 'console.log session',
      title: 'console.log session',
      signedInOnly: false,
      signedOutOnly: false,      
      debugOnly: true,
      action: this.consoleLogSession.bind(this)
    }, {
      icon: 'pest_control',
      name: 'nav tst',
      title: 'nav tst',
      signedInOnly: false,
      signedOutOnly: false,      
      debugOnly: true,
      action: this.navToTst.bind(this)
    }
  ]

  static styles = [
    ...ECComponent.styles,
    css`
      #titleBar, #titleBar * {
        background-color: var(--title-bar-bgcolor);
        color: var(--dark-fg);        
      }
      #titleBar {
        display: flex;
        width: 100%;
        align-items: center;
        box-sizing: border-box; /* include padding as content, preventing content overflow for 100% height/width settings */
        padding-top: 0.25em;
        padding-bottom: 0.25em;
      }
      #leftSide {
        flex: 1;          /* shorthand for: flex-grow: 1, flex-shrink: 1, flex-basis: 0 */
        display: flex;
        justify-content: flex-start;
        align-items: center;
        
        vertical-align: middle;
        font-size: 2rem;
        padding-left: 0.25em;
      }
      #appName {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        vertical-align: middle;
        font-size: 1.25rem;
      }
      div.timeStamp {
        font-size: 0.7rem;
      }
      #rightSide {
        flex: 1;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        text-align: right;
        vertical-align: middle;
        width: 10%;
        font-size: 1.5rem;
        padding-right: 0.25em;
      }
      #homeIcon {
        cursor: pointer;
      }
      a {
        text-decoration: none;
        color: var(--dark-fg);
      }
      a:visited {
        color: var(--dark-fg);
      }
    `
  ]

  constructor() {
    super()
    this.title = ''
  }
  
  render() {
    return html`
      <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet"/>
      <div id="titleBar">
        <div id="leftSide">
          <a href="/"><span id="homeIcon" title="Go to home page" class="material-icons">home</span></a>
        </div>
        <div id="appName" title="${CONFIG.buildTimestamp}">
          <div class="title">${this.title}</div>
          ${CONFIG.debug ? html`<div class="timeStamp" title="UI build timestamp">${CONFIG.buildTimestamp}</div>` : ''}
        </div>
        <div id="rightSide">
          <ec-menu title="User" .items=${this.userMenuItems} mainIconURL=${this.user?.avatarURL}></ec-menu>
        </div>
      </div>
    `;
  }
  private signUp(_e: PointerEvent) {
    Router.go('/users/_/signup')
  }
  private signIn(_e: PointerEvent) {
    Router.go('/users/_/signin')
  }
  private settings(_e: PointerEvent) {
    Router.go('/users/_/settings')
  }
  private signOut(_e: PointerEvent) {
    supabase.auth.signOut()
    Router.go('/')
  }
  private async consoleLogSession(_e: PointerEvent) {
    try {
      const { data, error } = await supabase.auth.getSession()
      console.log('-------- supabase.auth.getSession() --------')
      console.log(`session=`, data?.session)
      console.log(`session?.user=`, data?.session?.user)
      console.log('-------- ECSessionComponent properties --------')
      console.log(`session=`, this.user?.session)
      console.log(`signedIn=`, this.user?.signedIn)
      console.log(`avatarURL=`, this.user?.avatarURL)
      console.log('-------- globalThis properties --------')
      console.log(`globalThis=`, globalThis)

      console.log(`error`, error)
    } catch (error) {
      console.error(`Caught an error: `, error)
    }
  }
  private navToTst(_e: PointerEvent) {
    Router.go('/tst')
  }
}
